import { useCallback, useEffect } from 'react';

import { GridSortModel, GridValidRowModel } from '@mui/x-data-grid';
import { AsyncThunk } from '@reduxjs/toolkit';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import type { RootState } from 'app/store';
import { selectPaginationGridData, setPaginationGridData } from 'common/components/PalmDataGrid/palmDataGridSlice';
import { DISPLAYED_GRID_ROWS_COUNT } from 'common/constants/pagination';
import { extractStatusCode } from 'common/services/api/apiHelpers';
import { PaginatedRequestParamsBase } from 'common/services/api/Models/CommonRequestModels';
import { handleListFetchError } from 'common/utils/errorHandlers';
import { useSnackbar } from 'notistack';

export type ThunkResponse<TEntity> = {
    data: TEntity[];
    skipped: number;
    taken: number;
    count: number;
}

export function usePalmFetchPaginatedEntities<TThunkResponse extends ThunkResponse<TEntity>, TEntity extends GridValidRowModel, TThunkParams extends PaginatedRequestParamsBase>(
    entityName: string,
    fetchParams: TThunkParams,
    fetchEntitiesThunk: AsyncThunk<TThunkResponse, TThunkParams, Record<string, unknown>>,
    defaultSortModel: GridSortModel,
    setProcessing: (processing: boolean) => void,
    selectFilters: (state: RootState) => {
        keywords: string[];
    },
    deps: React.DependencyList = []
) {

    const dispatch = useAppDispatch();
    const { enqueueSnackbar } = useSnackbar();

    const paginationGridData = useAppSelector(selectPaginationGridData);
    const filters = useAppSelector(selectFilters);

    const rowsCount = DISPLAYED_GRID_ROWS_COUNT;

    const fetchGridData = useCallback((page: number, search?: string, model?: GridSortModel, signal?: AbortSignal) => {
        setProcessing(true);
        const params = {
            ...fetchParams,
            signal,
            search,
            skip: page * rowsCount,
            take: rowsCount,
            sortKey: model?.[0]?.field,
            sortOrder: model?.[0]?.sort
        } as TThunkParams;
        dispatch(fetchEntitiesThunk(params as TThunkParams & undefined))
            .unwrap()
            .then((response) => {
                setProcessing(false);
                dispatch(setPaginationGridData({
                    rows: response.data,
                    rowsCount: response.count,
                    currentPage: response.skipped / rowsCount,
                    rowsPerPage: rowsCount,
                    sortModel: model,
                }));
            })
            .catch((error) => {
                if (!signal?.aborted) {
                    setProcessing(false);
                    dispatch(setPaginationGridData({
                        rows: [],
                        rowsCount: 0,
                        currentPage: 0,
                        rowsPerPage: rowsCount,
                        sortModel: model ?? defaultSortModel,
                    }));
                    handleListFetchError(extractStatusCode(error.message), entityName, enqueueSnackbar);
                }
            });
    }, [defaultSortModel, dispatch, entityName, enqueueSnackbar, fetchEntitiesThunk, fetchParams, rowsCount, setProcessing]);

    useEffect(() => {
        const abortController = new AbortController();
        const { signal } = abortController;
        fetchGridData(0, filters.keywords[0], paginationGridData?.sortModel ?? defaultSortModel, signal);
        return () => {
            abortController.abort();
        };
    }, deps);

    return fetchGridData;

}
